/**
 * StatusPill: the Learning Center's status pill (plan docs/plans/lms-plan.md
 * section 13; the design skill's "Status pill" recipe: rounded-full px-2.5
 * py-0.5 text-xs font-medium, bg-{c}-100 text-{c}-800 dark:bg-{c}-900/40
 * dark:text-{c}-300, an optional bg-current dot). Color never carries the
 * meaning alone: every pill carries its word (13; the skill's color rules).
 *
 * Section 13 fixes the compliance colors: compliant green, expiring amber,
 * assigned primary, overdue red, expired red, waived gray, missing amber,
 * not_required gray, each with its word (lib/complianceStatus.ts
 * COMPLIANCE_STATUS_WORDS, the twin of the backend's status function).
 *
 * `Pill` is the bare recipe for the module's other states (a due state, a
 * transcript row's state, an external certificate's review state).
 */
import type { ReactNode } from 'react';
import { COMPLIANCE_STATUS_WORDS } from '../lib/complianceStatus';
import type { ComplianceStatus } from '../lib/complianceStatus';

export type PillTone = 'green' | 'amber' | 'red' | 'gray' | 'primary';

const TONE_CLS: Readonly<Record<PillTone, string>> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300',
  primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-300',
};

/** The pill's class string for a tone. */
export function pillToneCls(tone: PillTone): string {
  return `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLS[tone]}`;
}

export interface PillProps {
  tone: PillTone;
  children: ReactNode;
  /** The leading dot (h-1.5 w-1.5 rounded-full bg-current). */
  dot?: boolean;
  title?: string;
  'data-testid'?: string;
}

export function Pill({ tone, children, dot = true, title, 'data-testid': testId }: PillProps) {
  return (
    <span className={pillToneCls(tone)} title={title} data-tone={tone} data-testid={testId}>
      {dot ? <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/** Section 13's color for each compliance status. */
export const COMPLIANCE_STATUS_TONES: Readonly<Record<ComplianceStatus, PillTone>> = {
  compliant: 'green',
  expiring: 'amber',
  assigned: 'primary',
  overdue: 'red',
  expired: 'red',
  waived: 'gray',
  missing: 'amber',
  not_required: 'gray',
};

export interface StatusPillProps {
  status: ComplianceStatus;
  /** Replaces the status word (still paired with the status color). */
  label?: string;
  title?: string;
  'data-testid'?: string;
}

/** A compliance status as a pill with its word (13). An unknown status reads gray with its raw text. */
export function StatusPill({ status, label, title, 'data-testid': testId }: StatusPillProps) {
  const tone = COMPLIANCE_STATUS_TONES[status] ?? 'gray';
  const word = label ?? COMPLIANCE_STATUS_WORDS[status] ?? String(status);
  return (
    <Pill tone={tone} title={title} data-testid={testId}>
      {word}
    </Pill>
  );
}

export default StatusPill;
